/**
 * LevelExploreSheet — bottom-sheet del anillo de nivel del Home (plan 38 F3/F4).
 *
 * Deja al usuario **explorar** otro nivel CEFR temporalmente (adapta toda la
 * experiencia SIN tocar el real) y, si concluye que ese ES su nivel, **fijarlo**
 * como real (self-set, F4). Modela "elegido ≠ tuyo" como el LevelSheet de
 * /listen: marca "tu nivel" (real), el explorado activo (check) y ofrece volver.
 *
 * A0 (`Primeros pasos`) NO es explorable/fijable — es otra app entera (motor
 * Leitner, boss, estado persistente); el backend lo bloquea y acá ni se ofrece.
 *
 * `variant="sheet"` = bottom-sheet (móvil); `variant="modal"` = centrado (desktop).
 */
import { useState } from 'react';
import { useViewNavigate } from '../../../hooks/useViewNavigate.js';
import { CEFR_INFO } from '../../leveling/levelData.js';
import {
  EnglishApiError,
  type CefrLevel,
  type ExploreCefrLevel,
  type LevelOverlay,
} from '../../../lib/englishApi.js';
import { Waveform } from '../../../components/doodles/Doodles.js';
import { IconCheck, IconClose } from '../icons.js';
import { useSheetDrag } from '../../../hooks/useSheetDrag.js';

/** Copy user-facing por código de error del backend (409 con `.code`). */
const EXPLORE_ERROR_COPY: Record<string, string> = {
  in_graduate_welcome: 'Acabás de graduarte a A1 — dejá que se asiente unos días antes de explorar.',
  a0_cannot_explore: 'Explorar estará disponible cuando pases a A1.',
  a0_cannot_self_set: 'Fijar tu nivel estará disponible cuando pases a A1.',
  not_onboarded: 'Terminá tu nivelación antes de cambiar de nivel.',
  same_as_real_level: 'Ese ya es tu nivel.',
};

function cefrIdx(level: string): number {
  return CEFR_INFO.findIndex((l) => l.id === level);
}

function selfSetErrorCopy(err: unknown): string {
  if (err instanceof EnglishApiError) {
    if (err.code === 'cooldown') {
      const retryAt = err.details['retryAt'];
      if (typeof retryAt === 'string') {
        const days = Math.max(
          1,
          Math.ceil((new Date(retryAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
        );
        return `Cambiaste tu nivel hace poco. Podés volver a ajustarlo en ${days} ${days === 1 ? 'día' : 'días'}.`;
      }
      return 'Cambiaste tu nivel hace poco. Probá de nuevo en unos días.';
    }
    if (EXPLORE_ERROR_COPY[err.code]) return EXPLORE_ERROR_COPY[err.code]!;
  }
  return 'No pudimos fijar tu nivel. Probá de nuevo.';
}

function exploreErrorCopy(err: unknown): string {
  if (err instanceof EnglishApiError && EXPLORE_ERROR_COPY[err.code]) {
    return EXPLORE_ERROR_COPY[err.code]!;
  }
  return 'No pudimos cambiar el nivel. Probá de nuevo.';
}

interface LevelExploreSheetProps {
  /** Nivel CEFR REAL (identidad) — nunca cambia al explorar. */
  realLevel: CefrLevel;
  /** Overlay activo (`null` = no está explorando). */
  overlay: LevelOverlay | null;
  onExplore: (level: ExploreCefrLevel) => Promise<void>;
  onStop: () => Promise<void>;
  onSelfSet: (level: ExploreCefrLevel, applyRecommended?: boolean) => Promise<void>;
  onClose: () => void;
  variant?: 'sheet' | 'modal';
}

export function LevelExploreSheet({
  realLevel,
  overlay,
  onExplore,
  onStop,
  onSelfSet,
  onClose,
  variant = 'sheet',
}: LevelExploreSheetProps) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const navigate = useViewNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Nivel en confirmación de self-set (null = vista normal del selector).
  const [confirmLevel, setConfirmLevel] = useState<ExploreCefrLevel | null>(null);
  const [applyRec, setApplyRec] = useState(true);

  const exploring: ExploreCefrLevel | null = overlay?.level ?? null;
  const isA0 = realLevel === 'A0';
  const rows = CEFR_INFO.filter((l) => l.id !== 'A0');

  const run = async (fn: () => Promise<void>, errCopy: (e: unknown) => string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      onClose();
    } catch (err) {
      setMsg(errCopy(err));
      setBusy(false);
    }
  };

  const pick = (id: CefrLevel): void => {
    if (busy || isA0) return;
    if (id === realLevel) {
      if (exploring) void run(onStop, exploreErrorCopy);
      return;
    }
    if (id === exploring) return;
    void run(() => onExplore(id as ExploreCefrLevel), exploreErrorCopy);
  };

  const goProgress = (): void => {
    onClose();
    void navigate('/progress');
  };

  // Examen re-ofrecible (plan 28 §4.4): misma sesión calibration que la card de
  // /progress Totales. Descubribilidad F5: acá el user ya está mirando su nivel,
  // así que ofrecemos el examen preciso al lado del self-set (el atajo impreciso).
  const goExam = (): void => {
    onClose();
    void navigate('/conversation', { state: { exam: true } });
  };

  // ─── Vista de confirmación del self-set ──────────────────────────────────
  if (confirmLevel) {
    const jump = Math.abs(cefrIdx(confirmLevel) - cefrIdx(realLevel));
    return (
      <div
        className={`lx-overlay${variant === 'modal' ? ' is-modal' : ''}`}
        onClick={onClose}
        role="presentation"
      >
        <div
          className="lx-sheet"
          style={sheetStyle}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="bal-handle sheet-grab" {...grabProps} />
          <div className="lx-head">
            <span className="tag" style={{ color: 'var(--lime)' }}>
              ¿{confirmLevel} ES TU NIVEL?
            </span>
            <button type="button" className="lx-close" onClick={onClose} aria-label="Cerrar">
              <IconClose size={14} />
            </button>
          </div>
          <p className="lx-intro">
            Si sentís que la nivelación te ubicó mal, podés hacer <b>{confirmLevel}</b> tu nivel
            oficial. Tu radar, tu progreso y tus recomendaciones pasan a <b>{confirmLevel}</b>.
          </p>
          {jump > 1 && (
            <div className="lx-friction">
              Es un salto de <b>{jump} niveles</b> desde {realLevel}. Si no estás seguro, el examen
              de nivel es más preciso — podés hacerlo desde tu progreso.
            </div>
          )}
          <label className="lx-optin">
            <input type="checkbox" checked={applyRec} onChange={(e) => setApplyRec(e.target.checked)} />
            <span>Aplicar los ajustes recomendados de {confirmLevel} (intensidad del coach).</span>
          </label>
          {msg && (
            <div className="lx-msg" key={msg}>
              {msg}
            </div>
          )}
          <button
            type="button"
            className="lx-selfset-confirm"
            onClick={() => void run(() => onSelfSet(confirmLevel, applyRec), selfSetErrorCopy)}
            disabled={busy}
          >
            Sí, {confirmLevel} es mi nivel
          </button>
          <button
            type="button"
            className="lx-progress-link"
            onClick={() => {
              setConfirmLevel(null);
              setMsg(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  // ─── Vista normal del selector ───────────────────────────────────────────
  return (
    <div
      className={`lx-overlay${variant === 'modal' ? ' is-modal' : ''}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="lx-sheet"
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="bal-handle sheet-grab" {...grabProps} />
        <div className="lx-head">
          <span className="tag" style={{ color: 'var(--lime)' }}>
            NIVEL DE INGLÉS
          </span>
          <button type="button" className="lx-close" onClick={onClose} aria-label="Cerrar">
            <IconClose size={14} />
          </button>
        </div>

        {exploring ? (
          <div className="lx-exploring-banner">
            <div className="lx-exploring-tx">
              Estás explorando <b>{exploring}</b> · tu nivel real es <b>{realLevel}</b>
            </div>
            <div className="lx-exploring-actions">
              <button type="button" className="lx-back-btn" onClick={() => pick(realLevel)} disabled={busy}>
                Volver a mi nivel ({realLevel})
              </button>
              <button
                type="button"
                className="lx-selfset-btn"
                onClick={() => {
                  setApplyRec(true);
                  setConfirmLevel(exploring);
                }}
                disabled={busy}
              >
                {exploring} es mi nivel real →
              </button>
            </div>
          </div>
        ) : (
          <p className="lx-intro">
            Probá cómo se siente otro nivel. El coach, las correcciones y el contenido se ajustan al
            nivel que elijas — es <b>temporal</b> y <b>no cambia tu nivel real</b> ni tu progreso.
          </p>
        )}

        {isA0 ? (
          <div className="lx-a0-note">
            Explorar otros niveles estará disponible cuando pases a A1. Por ahora, seguí con tus
            Primeros pasos.
          </div>
        ) : (
          <div className="lx-rows">
            {rows.map((l) => {
              const isYou = l.id === realLevel;
              const isActive = l.id === exploring;
              return (
                <button
                  key={l.id}
                  type="button"
                  className={`lx-row${isActive ? ' is-active' : ''}${isYou ? ' is-you' : ''}`}
                  onClick={() => pick(l.id)}
                  disabled={busy}
                  aria-pressed={isActive}
                >
                  <span className="lx-row-badge">{l.id}</span>
                  <div className="lx-row-info">
                    <div className="lx-row-name">
                      {l.name}
                      {isYou && <span className="lx-you-pill">tu nivel</span>}
                    </div>
                    <div className="lx-row-blurb">{l.blurb}</div>
                  </div>
                  {isActive && (
                    <span className="lx-row-check" aria-label="Explorando">
                      <IconCheck size={18} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {msg && (
          <div className="lx-msg" key={msg}>
            {msg}
          </div>
        )}

        {!isA0 && !exploring && (
          <button type="button" className="lx-exam" onClick={goExam}>
            <span className="lx-exam-ic">
              <Waveform color="var(--purple)" size={16} />
            </span>
            <div className="lx-exam-tx">
              <div className="lx-exam-title">Afiná tu nivel · 3 min</div>
              <div className="lx-exam-sub">
                ¿No sabés bien tu nivel? Una charla corta conmigo y lo ajusto con precisión.
              </div>
            </div>
            <span className="lx-exam-arrow" aria-hidden="true">
              →
            </span>
          </button>
        )}

        <button type="button" className="lx-progress-link" onClick={goProgress}>
          Ver tu evolución completa →
        </button>
      </div>
    </div>
  );
}
